import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import LoadingScreen from '../../components/LoadingScreen';
import { CheckCircle, XCircle } from 'lucide-react';

export default function OnboardingComplete() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshBusiness } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'completing' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const processingRef = useRef(false);

  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const completeOnboarding = async () => {
      try {
        const sessionId = searchParams.get('session_id');

        if (!sessionId) {
          throw new Error('חסר מזהה סשן מסטרייפ');
        }

        if (!user) {
          throw new Error('משתמש לא מחובר');
        }

        setStatus('verifying');

        const { data: { session: authSession } } = await supabase.auth.getSession();
        if (!authSession) {
          throw new Error('אין סשן פעיל');
        }

        const verifyResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/VerifyStripeSession?session_id=${sessionId}`,
          {
            headers: {
              'Authorization': `Bearer ${authSession.access_token}`,
            }
          }
        );

        if (!verifyResponse.ok) {

        }

        const stripeSessionData = await verifyResponse.json().catch(() => null);

        const { data: appUser } = await supabase
          .from('users')
          .select('id, business_id, completed')
          .eq('id', user.id)
          .maybeSingle();

        if (!appUser) {
          const userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'מנהל';
          await supabase.from('users').upsert({
            id: user.id,
            name: userName,
            email: user.email || '',
            role: 'admin',
            completed: 0
          }, { onConflict: 'id' });
        }

        setStatus('completing');

        // Check if business exists from Stripe metadata OR from user record
        let existingBusinessId = appUser?.business_id || stripeSessionData?.metadata?.business_id;

        if (existingBusinessId && appUser?.completed === 1) {

          await refreshBusiness(user);
          setStatus('success');
          setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
          return;
        }

        // If business_id exists (from Stripe or DB), handle subscription sync
        if (existingBusinessId) {

          // Call sync function if we have subscription details
          if (stripeSessionData?.subscription_id && stripeSessionData?.customer_id && stripeSessionData?.price_id) {
            try {

              const { data: subData, error: subError } = await supabase.rpc('sync_stripe_subscription', {
                p_business_id: existingBusinessId,
                p_stripe_customer_id: stripeSessionData.customer_id,
                p_stripe_subscription_id: stripeSessionData.subscription_id,
                p_stripe_price_id: stripeSessionData.price_id,
                p_status: 'active',
                p_current_period_start: Math.floor(Date.now() / 1000),
                p_current_period_end: Math.floor(Date.now() / 1000) + (30 * 24 * 60 * 60),
                p_cancel_at_period_end: false,
                p_trial_end: null
              });

              if (subError) {

              } else {

              }
            } catch (syncErr) {

            }
          } else {

          }

          // Mark user as completed and update name
          const draftData = localStorage.getItem('registration_draft');
          let userName = user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'מנהל';

          if (draftData) {
            try {
              const registrationData = JSON.parse(draftData);
              userName = registrationData.step1?.name || userName;
            } catch (e) {
              console.error('Error parsing registration draft:', e);
            }
          }

          await supabase
            .from('users')
            .update({
              completed: 1,
              completed_at: new Date().toISOString(),
              business_id: existingBusinessId,
              name: userName
            })
            .eq('id', user.id);

          localStorage.setItem('completed', '1');
          await refreshBusiness(user);
          setStatus('success');
          setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
          return;
        }

        const draftData = localStorage.getItem('registration_draft');
        if (!draftData) {

        }

        let registrationData: any = {};
        if (draftData) {
          try {
            registrationData = JSON.parse(draftData);
          } catch (e) {

          }
        }

        const step2 = registrationData.step2;
        const step3 = registrationData.step3;
        const step4 = registrationData.step4;

        if (!step2?.name || !step2?.type) {
          throw new Error('חסרים פרטי עסק בסיסיים. אנא חזור לשלב 2 של ההרשמה');
        }

        if (!step3?.hours) {
          throw new Error('חסרות שעות פעילות. אנא חזור לשלב 3 של ההרשמה');
        }

        const { data: bookingLinkData, error: bookingError } = await supabase.rpc('generate_booking_link', {
          name: step2.name
        });

        if (bookingError || !bookingLinkData) {

          throw new Error('שגיאה ביצירת קישור הזמנה');
        }

        const { data: bizData, error: bizError } = await supabase
          .from('businesses')
          .insert({
            name: step2.name,
            description: step2.description || '',
            address: step2.address || '',
            settings: { theme: 'light', notifications: true },
            type: step2.type,
            booking_link: bookingLinkData
          })
          .select()
          .single();

        if (bizError || !bizData?.id) {

          throw new Error('שגיאה ביצירת העסק');
        }

        const businessId = bizData.id;
        localStorage.setItem('businessId', businessId);

        // Get step1 data for user name
        const step1 = registrationData.step1;
        const userName = step1?.name || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'מנהל';

        await supabase
          .from('users')
          .update({
            business_id: businessId,
            role: 'admin',
            name: userName
          })
          .eq('id', user.id);

        await new Promise(res => setTimeout(res, 500));

        await supabase
          .from('business_hours')
          .insert({
            business_id: businessId,
            regular_hours: step3.hours,
            special_dates: []
          });

        if (step4?.services?.length) {
          const services = step4.services.map((service: any) => ({
            business_id: businessId,
            name: service.name_he,
            name_he: service.name_he,
            price: parseFloat(service.price || '0'),
            duration: `${service.duration || '30'} minutes`,
            description: service.description || ''
          }));
          await supabase.from('services').insert(services);
        }

        // Check if subscription already exists from Stripe webhook
        try {
          const { data: existingSub } = await supabase
            .from('subscriptions')
            .select('id, stripe_subscription_id, stripe_customer_id, plan_id')
            .eq('business_id', businessId)
            .maybeSingle();

          if (existingSub) {

            localStorage.removeItem('selectedPlan');
            localStorage.removeItem('selectedPlanCycle');
            localStorage.removeItem('selectedPlanBusinessName');
          } else {
            // If no subscription exists (user didn't complete payment),
            // create a default subscription from localStorage
            const selectedPlan = localStorage.getItem('selectedPlan');
            const selectedPlanCycle = localStorage.getItem('selectedPlanCycle') || 'monthly';

            if (selectedPlan) {

              // Get plan_id from subscription_plans table
              const { data: planData, error: planError } = await supabase
                .from('subscription_plans')
                .select('id')
                .eq('code', selectedPlan)
                .maybeSingle();

              if (planError || !planData) {

              } else {
                // Create subscription record
                const { error: subError } = await supabase
                  .from('subscriptions')
                  .insert({
                    business_id: businessId,
                    plan_id: planData.id,
                    status: 'active',
                    billing_cycle: selectedPlanCycle,
                    current_period_start: new Date().toISOString(),
                    current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    is_active: true
                  });

                if (subError) {

                } else {

                  localStorage.removeItem('selectedPlan');
                  localStorage.removeItem('selectedPlanCycle');
                  localStorage.removeItem('selectedPlanBusinessName');
                }
              }
            } else {

            }
          }
        } catch (subErr) {

        }

        await supabase
          .from('users')
          .update({ completed: 1, completed_at: new Date().toISOString() })
          .eq('id', user.id);

        localStorage.setItem('completed', '1');
        localStorage.removeItem('registration_draft');

        await refreshBusiness(user);

        setStatus('success');
        toast.success('ההרשמה הושלמה בהצלחה!');

        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);

      } catch (error: any) {

        setStatus('error');
        setErrorMessage(error.message || 'שגיאה לא ידועה');
        toast.error(error.message || 'שגיאה בהשלמת ההרשמה');
      }
    };

    completeOnboarding();
  }, [user, searchParams, navigate, refreshBusiness]);

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center space-y-6 p-8">
          <CheckCircle className="h-24 w-24 text-green-500 mx-auto animate-bounce" />
          <h1 className="text-3xl font-bold text-gray-900">ההרשמה הושלמה בהצלחה!</h1>
          <p className="text-gray-600">מעביר אותך לדשבורד...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md">
          <XCircle className="h-24 w-24 text-red-500 mx-auto" />
          <h1 className="text-3xl font-bold text-gray-900">אופס! משהו השתבש</h1>
          <p className="text-gray-600">{errorMessage}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/register/business?step=2', { replace: true })}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
            >
              חזור להרשמה
            </button>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition"
            >
              נסה להיכנס לדשבורד
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <LoadingScreen
      autoNavigate={false}
      message={status === 'verifying' ? 'מאמת תשלום...' : 'משלים הרשמה...'}
    />
  );
}
